import { useEffect, useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Download, ShoppingCart, Loader2, ArrowDown, Check } from "lucide-react";
import { fetchProductByHandle } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";
import { trackCtaClick, trackBeginCheckout } from "@/lib/analytics";
import bookCover from "@/assets/founder-laws-cover.jpg";
import PageSeo from "@/components/PageSeo";
import { useLanguage } from "@/i18n/LanguageContext";


const PRODUCT_HANDLE = "the-founder-laws";

interface ShopifyProductNode {
  id: string;
  title: string;
  description: string;
  handle: string;
  tags: string[];
  priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
  images: { edges: Array<{ node: { url: string; altText: string | null } }> };
  variants: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        price: { amount: string; currencyCode: string };
        availableForSale: boolean;
        selectedOptions: Array<{ name: string; value: string }>;
      };
    }>;
  };
  options: Array<{ name: string; values: string[] }>;
}

const FounderLaws = () => {
  const [product, setProduct] = useState<ShopifyProductNode | null>(null);
  const [loading, setLoading] = useState(true);
  const addItem = useCartStore((s) => s.addItem);
  const isCartLoading = useCartStore((s) => s.isLoading);
  const { t } = useLanguage();

  useEffect(() => {
    let cancelled = false;
    fetchProductByHandle(PRODUCT_HANDLE)
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
      })
      .catch(() => {
        if (!cancelled) toast.error(t("book.toast.loadError"));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const variant = product?.variants.edges[0]?.node;
  const price = variant?.price.amount ? parseFloat(variant.price.amount).toFixed(0) : "29";
  const currency = variant?.price.currencyCode || "USD";

  const handleBuy = async (ctaId: string) => {
    trackCtaClick(ctaId, {
      page: "founder_laws",
      product_handle: PRODUCT_HANDLE,
      price: Number(price),
      currency,
      product_ready: Boolean(product && variant),
    });

    let activeProduct = product;
    let activeVariant = variant;

    // If product hasn't loaded yet (slow network / earlier failure), retry now
    if (!activeProduct || !activeVariant) {
      try {
        setLoading(true);
        const fetched = await fetchProductByHandle(PRODUCT_HANDLE);
        if (fetched) {
          setProduct(fetched);
          activeProduct = fetched;
          activeVariant = fetched.variants?.edges?.[0]?.node;
        }
      } catch {
        // fall through to error toast below
      } finally {
        setLoading(false);
      }
    }

    if (!activeProduct || !activeVariant) {
      toast.error(t("book.toast.checkoutError"));
      return;
    }

    trackBeginCheckout(
      [
        {
          item_id: activeVariant.id,
          item_name: activeProduct.title,
          price: parseFloat(activeVariant.price.amount),
          quantity: 1,
          currency: activeVariant.price.currencyCode,
        },
      ],
      { page: "founder_laws", cta_id: ctaId },
    );

    await addItem({
      product: { node: activeProduct },
      variantId: activeVariant.id,
      variantTitle: activeVariant.title,
      price: activeVariant.price,
      quantity: 1,
      selectedOptions: activeVariant.selectedOptions || [],
    });
    toast.success(t("book.toast.addedToCart"));
  };

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const PrimaryCTA = ({
    label = t("book.cta.getInstantAccess").replace("${price}", price),
    ctaId,
  }: {
    label?: string;
    ctaId: string;
  }) => (
    <Button
      onClick={() => handleBuy(ctaId)}
      disabled={isCartLoading}
      size="xl"
      variant="accent"
      className="font-sans group"
      data-cta-id={ctaId}
    >
      {isCartLoading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          <Download className="mr-2 h-5 w-5" />
          {label}
        </>
      )}
    </Button>
  );

  const SecondaryCTA = () => (
    <Button
      onClick={() => scrollTo("whats-inside")}
      size="xl"
      variant="outline"
      className="font-sans"
    >
      {t("book.cta.seeWhatsInside")}
      <ArrowDown className="ml-2 h-4 w-4" />
    </Button>
  );

  const pillars = [
    { n: "01", title: t("book.pillars.0.title"), body: t("book.pillars.0.body") },
    { n: "02", title: t("book.pillars.1.title"), body: t("book.pillars.1.body") },
    { n: "03", title: t("book.pillars.2.title"), body: t("book.pillars.2.body") },
    { n: "04", title: t("book.pillars.3.title"), body: t("book.pillars.3.body") },
    { n: "05", title: t("book.pillars.4.title"), body: t("book.pillars.4.body") },
  ];

  const benefits = [
    t("book.benefits.list.0"),
    t("book.benefits.list.1"),
    t("book.benefits.list.2"),
    t("book.benefits.list.3"),
    t("book.benefits.list.4"),
    t("book.benefits.list.5"),
  ];

  const audience = [
    t("book.audience.list.0"),
    t("book.audience.list.1"),
    t("book.audience.list.2"),
    t("book.audience.list.3"),
    t("book.audience.list.4"),
  ];

  const faqs = [
    { q: t("book.faq.0.q"), a: t("book.faq.0.a") },
    { q: t("book.faq.1.q"), a: t("book.faq.1.a") },
    { q: t("book.faq.2.q"), a: t("book.faq.2.a") },
    { q: t("book.faq.3.q"), a: t("book.faq.3.a") },
    { q: t("book.faq.4.q"), a: t("book.faq.4.a") },
    { q: t("book.faq.5.q"), a: t("book.faq.5.a").replace("${price}", price).replace("{currency}", currency) },
    { q: t("book.faq.6.q"), a: t("book.faq.6.a") },
  ];

  return (
    <main className="min-h-screen bg-background">
      <PageSeo
        title={t("seo.book.title")}
        description={t("seo.book.description")}
        path="/founder-laws"
        type="product"
        image={`https://petermdingle.com${bookCover}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: t("seo.book.title"),
            description: t("seo.book.description"),
            image: `https://petermdingle.com${bookCover}`,
            brand: { "@type": "Brand", name: "Peter M Dingle" },
            offers: {
              "@type": "Offer",
              price,
              priceCurrency: currency,
              availability: "https://schema.org/InStock",
              url: "https://petermdingle.com/founder-laws",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />
      <Navigation />



      {/* Top bar */}
      <div className="bg-foreground text-background text-center text-xs sm:text-sm font-medium py-2.5 px-4 tracking-wide font-sans pt-24">
        {t("book.topbar").replace("${price}", price).replace("{currency}", currency)}
      </div>

      {/* Hero */}
      <section className="py-20 sm:py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <div className="space-y-7 order-2 lg:order-1">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                {t("book.hero.eyebrow")}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
                {t("book.hero.title")}
              </h1>
              <p className="text-xl sm:text-2xl text-muted-foreground font-serif">
                {t("book.hero.subtitle")}
              </p>
              <p className="text-lg text-foreground/80 font-serif leading-relaxed">
                {t("book.hero.lede")}
              </p>
              <p className="text-base text-muted-foreground font-serif leading-relaxed">
                {t("book.hero.body")}
                <br />
                <span className="text-foreground font-medium">{t("book.hero.bodyStrong")}</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <PrimaryCTA ctaId="founder_laws_hero_primary" />
                <SecondaryCTA />
              </div>
              <p className="text-sm text-muted-foreground font-sans flex items-center gap-2 pt-2">
                <Check className="w-4 h-4 text-accent" />
                {t("book.hero.trust")}
              </p>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <img
                src={bookCover}
                alt={t("book.hero.coverAlt")}
                width={928}
                height={1152}
                className="w-full max-w-md drop-shadow-2xl rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-24 sm:py-28 bg-secondary/30 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("book.problem.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("book.problem.title")}
            </h2>
            <div className="space-y-5 text-lg text-muted-foreground font-serif leading-relaxed">
              <p>{t("book.problem.p1")}</p>
              <p>{t("book.problem.p2")}</p>
              <p className="text-foreground font-medium">{t("book.problem.p3")}</p>
              <ul className="space-y-2 pl-6 list-disc marker:text-accent">
                <li>{t("book.problem.list.0")}</li>
                <li>{t("book.problem.list.1")}</li>
                <li>{t("book.problem.list.2")}</li>
                <li>{t("book.problem.list.3")}</li>
                <li>{t("book.problem.list.4")}</li>
                <li>{t("book.problem.list.5")}</li>
              </ul>
            </div>
            <div className="pt-4">
              <PrimaryCTA ctaId="founder_laws_problem" label={t("book.cta.buyForPrice").replace("${price}", price)} />
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section id="whats-inside" className="py-24 sm:py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-14">
            <div className="space-y-6 max-w-3xl">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                {t("book.inside.eyebrow")}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                {t("book.inside.title")}
              </h2>
              <p className="text-lg text-muted-foreground font-serif">
                {t("book.inside.lede")}
              </p>
            </div>

            {/* AI Prompt highlight */}
            <div className="relative overflow-hidden border border-accent/30 bg-gradient-to-br from-accent/10 via-background to-background p-8 sm:p-10 rounded-lg">
              <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                <div className="space-y-4">
                  <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                    {t("book.ai.eyebrow")}
                  </p>
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
                    {t("book.ai.title")}
                  </h3>
                  <p className="text-base sm:text-lg text-muted-foreground font-serif leading-relaxed max-w-2xl">
                    {t("book.ai.body")}
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 pt-2">
                    {[
                      t("book.ai.list.0"),
                      t("book.ai.list.1"),
                      t("book.ai.list.2"),
                      t("book.ai.list.3"),
                    ].map((item) => (
                      <li key={item} className="flex gap-2 text-sm text-foreground/90 font-sans">
                        <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="hidden lg:flex items-center justify-center">
                  <div className="font-mono text-xs bg-foreground text-background p-5 rounded-md shadow-xl max-w-xs space-y-2 border border-border/20">
                    <div className="text-accent-mint">{t("book.ai.promptLabel")}</div>
                    <div className="text-background/90 leading-relaxed">
                      {t("book.ai.promptText")}
                    </div>
                    <div className="text-background/40 pt-1">▍</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60 border border-border/60">
              {pillars.map((p) => (
                <div key={p.n} className="bg-background p-8 space-y-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-xs font-mono text-accent tracking-widest">{p.n}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/70">
                      {t("book.pillars.tag")}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold tracking-tight">{p.title}</h3>
                  <p className="text-muted-foreground font-serif leading-relaxed">{p.body}</p>
                </div>
              ))}
              <div className="bg-secondary/40 p-8 space-y-3 flex flex-col justify-center">
                <p className="text-xs font-mono text-accent tracking-widest">∑</p>
                <h3 className="text-xl font-bold tracking-tight">{t("book.pillars.summary.title")}</h3>
                <p className="text-muted-foreground font-serif leading-relaxed text-sm">
                  {t("book.pillars.summary.body")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why it converts / Benefits */}
      <section className="py-24 sm:py-28 bg-secondary/30 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("book.benefits.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("book.benefits.title")}
            </h2>
            <div className="space-y-2 text-lg text-foreground/90 font-serif">
              <p>{t("book.benefits.line.0")}</p>
              <p>{t("book.benefits.line.1")}</p>
              <p>{t("book.benefits.line.2")}</p>
              <p>{t("book.benefits.line.3")}</p>
              <p>{t("book.benefits.line.4")}</p>
            </div>
            <p className="text-lg text-muted-foreground font-serif leading-relaxed pt-2">
              {t("book.benefits.body")}
            </p>

            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 pt-4">
              {benefits.map((b) => (
                <li key={b} className="flex gap-3 text-foreground/90 font-serif">
                  <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="pt-6">
              <PrimaryCTA ctaId="founder_laws_benefits" label={t("book.cta.downloadNow").replace("${price}", price)} />
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-24 sm:py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("book.audience.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("book.audience.title")}
            </h2>
            <ul className="space-y-4 pt-2">
              {audience.map((a) => (
                <li
                  key={a}
                  className="flex gap-4 text-lg text-foreground/90 font-serif border-b border-border/40 pb-4"
                >
                  <span className="text-accent font-mono text-sm pt-1.5">→</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
            <p className="text-base text-muted-foreground font-serif italic pt-2">
              {t("book.audience.closing")}
            </p>
          </div>
        </div>
      </section>

      {/* What makes it different */}
      <section className="py-24 sm:py-28 bg-secondary/30 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
              {t("book.different.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              {t("book.different.title")}
            </h2>
            <div className="space-y-5 text-lg text-muted-foreground font-serif leading-relaxed">
              <p>{t("book.different.p1")}</p>
              <p>{t("book.different.p2")}</p>
              <p className="text-foreground font-medium">
                {t("book.different.p3Strong")}
              </p>
              <p>{t("book.different.p4")}</p>
              <ul className="space-y-2 pl-6 list-disc marker:text-accent">
                <li>{t("book.different.list.0")}</li>
                <li>{t("book.different.list.1")}</li>
                <li>{t("book.different.list.2")}</li>
                <li>{t("book.different.list.3")}</li>
                <li>{t("book.different.list.4")}</li>
                <li>{t("book.different.list.5")}</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <PrimaryCTA ctaId="founder_laws_workbook" label={t("book.cta.getEbook").replace("${price}", price)} />
              <SecondaryCTA />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 sm:py-28 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-10">
            <div className="space-y-4">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent">
                {t("book.faq.eyebrow")}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
                {t("book.faq.title")}
              </h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-lg font-bold tracking-tight font-sans">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground font-serif leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-28 sm:py-32 pb-40 md:pb-32 bg-gradient-to-br from-primary via-foreground/95 to-foreground/90 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-accent-mint">
              {t("book.final.eyebrow")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.1]">
              {t("book.final.title")}
            </h2>
            <div className="space-y-4 text-lg text-white/80 font-serif leading-relaxed max-w-2xl mx-auto">
              <p>{t("book.final.p1")}</p>
              <p className="text-white font-medium">
                {t("book.final.p2").replace("${price}", price)}
              </p>
            </div>

            <div className="text-sm text-white/60 font-serif italic space-y-1 pt-2">
              <p>{t("book.final.line.0")}</p>
              <p>{t("book.final.line.1")}</p>
              <p>{t("book.final.line.2")}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-6">
              <Button
                onClick={() => handleBuy("founder_laws_final_get_instant_access")}
                disabled={loading || isCartLoading || !variant}
                size="xl"
                variant="hero"
                className="font-sans w-full sm:w-auto whitespace-nowrap"
                data-cta-id="founder_laws_final_get_instant_access"
              >
                {loading || isCartLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Download className="mr-2 h-5 w-5" />
                    {t("book.cta.getInstantAccess").replace("${price}", price)}
                  </>
                )}
              </Button>
              <Button
                onClick={() => handleBuy("founder_laws_final_buy_now")}
                disabled={loading || isCartLoading || !variant}
                size="xl"
                variant="outline"
                className="font-sans w-full sm:w-auto whitespace-nowrap bg-transparent border-white/40 text-white hover:bg-white hover:text-foreground"
                data-cta-id="founder_laws_final_buy_now"
              >
                {loading || isCartLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    {t("book.cta.buyNow")}
                  </>
                )}
              </Button>
            </div>

            <p className="text-sm text-white/60 font-sans flex items-center justify-center gap-2 pt-4">
              <Check className="w-4 h-4 text-accent-mint" />
              {t("book.final.trust")}
            </p>
          </div>
        </div>
      </section>

      {/* Sticky mobile buy bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-lg border-t border-border p-3 shadow-lg">
        <Button
          onClick={() => handleBuy("founder_laws_sticky_mobile")}
          disabled={loading || isCartLoading || !variant}
          size="lg"
          variant="accent"
          className="w-full font-sans"
          data-cta-id="founder_laws_sticky_mobile"
        >
          {loading || isCartLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Download className="mr-2 h-5 w-5" />
              {t("book.cta.getInstantAccess").replace("${price}", price)}
            </>
          )}
        </Button>
      </div>

      <Footer />
    </main>
  );
};

export default FounderLaws;
